import type { BoardCaregiver, VisitDetail } from '@homecare/contracts';
import { Button, CloseMark, EmptyState, Eyebrow, Spinner, StatusPill } from '@homecare/ui';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { dayText, instantText, minutesText } from '../../lib/schedule-time';

import { boardState } from './board-layout';
import { VisitActions } from './visit-actions';

/**
 * The visit drawer (slice-03 §8): assignment, address, care plan tasks,
 * overrides, history — who did what and when — and the actions.
 *
 * On an open shift **Find a caregiver** is the primary action, because that
 * is the only question an open shift asks. On a booked one there is no
 * primary action: nothing is wrong with it.
 *
 * Times are the visit's local fields as the API computed them, in the service
 * location's zone; the history's instants go through `instantText` with the
 * agency's zone. Nothing here formats a date by itself (RECOVERY §2).
 */
export function VisitDrawer({
  visitId,
  timeZone,
  caregivers,
  onClose,
}: {
  visitId: string | null;
  timeZone: string;
  caregivers: readonly BoardCaregiver[];
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const detail = useQuery({
    queryKey: ['visits', visitId],
    queryFn: () => api.get<VisitDetail>(`/v1/visits/${visitId ?? ''}`),
    enabled: visitId !== null,
    staleTime: 0,
  });
  const visit = detail.data;

  return (
    <Dialog.Root open={visitId !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-overlay fixed inset-0 z-40" />
        <Dialog.Content
          className="border-line bg-surface shadow-e4 fixed bottom-0 right-0 top-0 z-50 grid w-[min(34rem,100vw)] grid-rows-[auto_1fr] border-l"
          data-testid="visit-drawer"
          aria-describedby={undefined}
        >
          <header className="border-line flex items-start justify-between gap-4 border-b p-5">
            <div className="min-w-0">
              <Dialog.Title className="text-ink text-lg font-bold">
                {visit ? visit.clientName : 'Visit'}
              </Dialog.Title>
              {visit && (
                <p className="text-ink-3 mt-1 text-sm">
                  {dayText(visit.localDate)}, {visit.localStartTime}–{visit.localEndTime}
                  {visit.crossesMidnight ? ' (ends the next morning)' : ''} ·{' '}
                  {minutesText(visit.scheduledMinutes)} · {visit.serviceCode}
                </p>
              )}
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm" aria-label="Close" icon={<CloseMark />} />
            </Dialog.Close>
          </header>

          <div className="min-h-0 overflow-y-auto p-5">
            {detail.isPending ? (
              <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
                <Spinner /> Loading the visit…
              </p>
            ) : detail.isError ? (
              <ErrorNote
                error={detail.error}
                nextStep="Close this and open the visit again from the board."
              />
            ) : visit ? (
              <VisitBody visit={visit} timeZone={timeZone} caregivers={caregivers} />
            ) : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function VisitBody({
  visit,
  timeZone,
  caregivers,
}: {
  visit: VisitDetail;
  timeZone: string;
  caregivers: readonly BoardCaregiver[];
}): ReactNode {
  const state = boardState(visit);

  return (
    <div className="grid gap-6">
      <section aria-labelledby="visit-assignment" className="grid gap-2">
        <div className="flex items-center justify-between gap-3">
          <h3 id="visit-assignment" className="text-ink font-semibold">
            Assignment
          </h3>
          <StatusPill tone={state.tone}>{state.label}</StatusPill>
        </div>
        <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-ink-3">Caregiver</dt>
          <dd className="text-ink">{visit.caregiverName ?? 'Nobody yet — an open shift'}</dd>
          <dt className="text-ink-3">Where</dt>
          <dd className="text-ink">
            {visit.address ?? 'The address is visible to people who may read client details.'}
          </dd>
          {visit.timezone !== timeZone && (
            <>
              <dt className="text-ink-3">Time zone</dt>
              <dd className="text-ink">Times are local to the visit: {visit.timezone}</dd>
            </>
          )}
          {visit.cancellationReasonCode && (
            <>
              <dt className="text-ink-3">Cancelled</dt>
              <dd className="text-ink">
                {visit.cancellationReasonCode.toLowerCase().replaceAll('_', ' ')}
              </dd>
            </>
          )}
        </dl>
      </section>

      <VisitActions visit={visit} timeZone={timeZone} caregivers={caregivers} />

      <section aria-labelledby="visit-tasks" className="grid gap-2">
        <h3 id="visit-tasks" className="text-ink font-semibold">
          Care plan tasks
        </h3>
        {visit.tasks.length === 0 ? (
          <EmptyState
            title="No tasks on this visit"
            explanation="Tasks come from the client's active care plan when the visit is booked. Add tasks to the care plan, and visits booked after that carry them."
          />
        ) : (
          <ul className="grid gap-1 text-sm">
            {visit.tasks.map((task) => (
              <li key={task.id} className="border-line rounded-md border px-3 py-2">
                <span className="text-ink font-medium">{task.name}</span>
                {task.isRequired && <span className="text-ink-3"> · required</span>}
                {task.instructions && (
                  <span className="text-ink-2 block text-xs">{task.instructions}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {visit.overrides.length > 0 && (
        <section aria-labelledby="visit-overrides" className="grid gap-2">
          <h3 id="visit-overrides" className="text-ink font-semibold">
            Rules overridden
          </h3>
          <ul className="grid gap-1 text-sm">
            {visit.overrides.map((entry) => (
              <li key={entry.id} className="border-line rounded-md border px-3 py-2">
                <Eyebrow>
                  Rule {entry.rule} · {entry.code}
                </Eyebrow>
                <span className="text-ink block">“{entry.reason}”</span>
                <span className="text-ink-3 text-xs">
                  {entry.overriddenByName ?? 'Someone'} · {instantText(entry.createdAt, timeZone)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="visit-history" className="grid gap-2">
        <h3 id="visit-history" className="text-ink font-semibold">
          History
        </h3>
        {visit.history.length === 0 ? (
          <EmptyState
            title="No history recorded"
            explanation="Every booking, change, call-out and cancellation is written here with who did it and when."
          />
        ) : (
          <ol className="grid gap-1 text-sm">
            {visit.history.map((entry) => (
              <li key={entry.id} className="flex justify-between gap-3">
                <span className="text-ink">
                  {entry.summary}
                  <span className="text-ink-3"> · {entry.actorName ?? 'the system'}</span>
                  {entry.reason && (
                    <span className="text-ink-2 block text-xs">“{entry.reason}”</span>
                  )}
                </span>
                <span className="text-ink-3 whitespace-nowrap text-xs">
                  {instantText(entry.occurredAt, timeZone)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
